import { Link } from 'react-router-dom';
import { cn } from '../../lib/format.js';
import { promoTheme } from '../../lib/promoThemes.js';

/**
 * The sale block on the home page.
 *
 * No animation of any kind, deliberately. This band exists to be seen, and it
 * has already been invisible twice — once because `whileInView` never fires
 * under the motion feature set this app loads, and once because the entrance it
 * was changed to still started from `opacity: 0`. Both times the copy was sitting
 * right there in the markup while the page showed an empty coloured strip.
 *
 * There is nothing left to fail: it is plain markup with no transform, no
 * opacity, no observer, and no dependency on JavaScript having run. An entrance
 * effect was never worth the risk of a sale nobody can read.
 *
 * Entirely operator-owned: heading, body, eyebrow, button, link, colour, and
 * whether it appears at all. Nothing here is hardcoded, because the point of
 * building it was that a sale can start on a Friday afternoon without anyone
 * touching the repository.
 *
 * The shape follows the rest of the home page — a centred stack, the eyebrow in
 * small capitals between hairlines, square corners — while the colour stays the
 * operator's choice of theme. It is the loudest thing on the page by design, and
 * that contrast is what makes it read as an offer.
 */

const CTA_CLASSES =
  'inline-flex items-center bg-white px-7 py-3.5 text-2xs font-semibold tracking-[0.12em] text-ink uppercase transition-colors hover:bg-white/85 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white';

export const PromoBanner = ({ promotions = {} }) => {
  if (!promotions.bannerEnabled || !promotions.bannerHeading) return null;

  const theme = promoTheme(promotions.bannerTheme);
  const href = promotions.bannerCtaHref;
  const external = /^https?:\/\//i.test(href ?? '');

  return (
    <section aria-label="Offer" className={cn('relative overflow-hidden', theme.solid)}>
      {/* Diagonal rules, drawn in the band's own foreground at low opacity so
          they work under every theme without a per-theme value. Decorative. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:repeating-linear-gradient(-45deg,currentColor_0_1px,transparent_1px_14px)]"
      />

      <div className="relative mx-auto flex max-w-3xl flex-col items-center px-4 py-14 text-center sm:px-6 lg:py-16">
        {promotions.bannerEyebrow && (
          <p
            className={cn(
              'flex items-center gap-3 text-2xs font-semibold tracking-[0.18em] uppercase',
              theme.solidMuted,
            )}
          >
            <span aria-hidden="true" className="h-px w-8 bg-current" />
            {promotions.bannerEyebrow}
            <span aria-hidden="true" className="h-px w-8 bg-current" />
          </p>
        )}

        <h2 className="mt-3 font-display text-2xl leading-[1.08] font-medium text-balance text-current uppercase lg:text-3xl">
          {promotions.bannerHeading}
        </h2>

        {promotions.bannerBody && (
          <p className={cn('mt-4 max-w-xl text-sm leading-relaxed', theme.solidMuted)}>{promotions.bannerBody}</p>
        )}

        {promotions.bannerCtaLabel && href && (
          <div className="mt-7">
            {/* White on the band rather than the store's gold, which would
                disappear into the gold theme and clash with every other one. */}
            {external ? (
              <a href={href} className={CTA_CLASSES} target="_blank" rel="noopener noreferrer">
                {promotions.bannerCtaLabel}
              </a>
            ) : (
              <Link to={href} className={CTA_CLASSES}>
                {promotions.bannerCtaLabel}
              </Link>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default PromoBanner;
